import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Hook to handle animated modal closing
 * @param {Function} onClose - The original onClose callback
 * @param {number} animationDuration - Duration of the exit animation in ms (default: 200)
 * @returns {Object} { isClosing, handleClose }
 */
export function useModalClose(onClose, animationDuration = 200) {
  const [isClosing, setIsClosing] = useState(false);
  const timeoutRef = useRef(null);

  const handleClose = useCallback(() => {
    // Start closing animation
    setIsClosing(true);

    // Wait for animation to complete, then call onClose
    timeoutRef.current = setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, animationDuration);
  }, [onClose, animationDuration]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Reset isClosing when modal reopens
  useEffect(() => {
    setIsClosing(false);
  }, [onClose]);

  return { isClosing, handleClose };
}

export default useModalClose;
